import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY_NAME } from "@/lib/api";
import { ArrowLeft, Search, User } from "lucide-react";
import { ClipCardWithActions } from "@/components/ClipCard";

export default function HorseProfile() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get(`/horse/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load horse"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [name]);

  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.events;
    return data.events
      .map((ev) => ({
        ...ev,
        clips: ev.clips.filter((c) => {
          const hay = [
            c.rider_horse_name, c.heeler_name, c.owner_name,
            c.sub_label, c.notes, c.source_video_name,
            ev.event_name, CATEGORY_NAME[c.category] || c.category,
          ].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((ev) => ev.clips.length > 0);
  }, [data, search]);

  if (loading) {
    return <div className="px-6 md:px-12 py-12 text-stone-500" data-testid="horse-loading">Loading…</div>;
  }
  if (err || !data) {
    return (
      <div className="px-6 md:px-12 py-12" data-testid="horse-error">
        <Link to="/horses" className="text-stone-500 hover:text-orange-400 text-sm inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back to Horses
        </Link>
        <div className="border border-rose-900/40 bg-rose-950/20 text-rose-300 p-6">{err || "Not found"}</div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="horse-profile">
      <Link to="/horses" className="text-stone-500 hover:text-orange-400 text-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> All horses
      </Link>
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Horse</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">🐎 {data.name}</h1>
        <div className="flex flex-wrap gap-6 mt-4 text-sm text-stone-400">
          <div><span className="text-orange-500 font-bold">{data.total_clips}</span> clips</div>
          <div><span className="text-orange-500 font-bold">{data.avg_duration}s</span> avg run</div>
          <div><span className="text-orange-500 font-bold">{data.clean_pct}%</span> clean runs</div>
          <div><span className="text-orange-500 font-bold">{data.riders.length}</span> rider{data.riders.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {data.riders.length > 0 && (
        <div className="mb-6">
          <div className="label-tiny mb-3">Ridden by</div>
          <div className="flex flex-wrap gap-2" data-testid="horse-riders">
            {data.riders.map((r) => (
              <Link
                key={r}
                to={`/rider/${encodeURIComponent(r)}`}
                className="inline-flex items-center gap-1.5 border border-stone-800 hover:border-orange-700 px-3 py-1.5 text-sm transition-colors"
                data-testid={`horse-rider-${r}`}
              >
                <User size={12} /> {r}
              </Link>
            ))}
          </div>
        </div>
      )}

      {data.events.length > 0 && (
        <div className="mb-8 max-w-md">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rider, event, notes…"
              className="field-input w-full !pl-9"
              data-testid="horse-clip-search"
            />
          </div>
        </div>
      )}

      {filteredEvents.length === 0 ? (
        search ? (
          <div className="border border-dashed border-stone-800 p-8 text-center text-stone-500">
            No clips match "{search}"
          </div>
        ) : null
      ) : filteredEvents.map((ev) => (
        <div key={`${ev.event_name}-${ev.event_date}`} className="mb-10" data-testid={`horse-event-${ev.event_name}`}>
          <div className="flex items-baseline justify-between border-b border-stone-800 pb-2 mb-4">
            <div className="font-display uppercase font-bold text-2xl">{ev.event_name}</div>
            <div className="text-stone-500 text-xs font-mono-tc">{ev.event_date || "—"} · {ev.clips.length} clip{ev.clips.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ev.clips.map((c) => (
              <ClipCardWithActions key={c.id} clip={c} onChanged={load} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
